import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCustomer } from './Detail';
import { User, Phone, MapPin, QrCode, CheckCircle, Loader2 } from 'lucide-react';
import { DeliveryMap, type CustomerMarkerData } from '@/components/DeliveryMap';
import { getAddress, getCustomerSignupQr } from '@vn-propane/shared/client';
import type { Address, ErrorResponse } from '@vn-propane/shared/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export default function CustomerOverview() {
  const { id: orgId } = useParams<{ id: string }>();
  const { t } = useTranslation(['admin', 'common']);
  const { customer } = useCustomer();
  const [address, setAddress] = useState<Address | null>(null);
  const [customerMarker, setCustomerMarker] = useState<CustomerMarkerData | null>(null);
  const [showQrDialog, setShowQrDialog] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrData, setQrData] = useState<{ signupUrl: string; qrCodeBase64: string } | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);

  const handleGenerateQr = async () => {
    if (!orgId || !customer) return;

    setShowQrDialog(true);
    setQrLoading(true);
    setQrError(null);
    setQrData(null);

    try {
      const result = await getCustomerSignupQr({
        path: { org_id: orgId, id: customer.id },
      });

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        setQrError(errorResponse.error?.message || t('customers.qrCodeError'));
        return;
      }

      if (result.data?.data) {
        setQrData({
          signupUrl: result.data.data.signup_url,
          qrCodeBase64: result.data.data.qr_code_base64,
        });
      }
    } catch {
      setQrError(t('customers.qrCodeError'));
    } finally {
      setQrLoading(false);
    }
  };

  // Fetch address if customer has addressId
  useEffect(() => {
    async function fetchAddress() {
      if (!orgId || !customer?.addressId) {
        setAddress(null);
        return;
      }

      try {
        const result = await getAddress({
          path: { org_id: orgId, id: customer.addressId },
        });
        if (result.data?.data) {
          setAddress(result.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch address:', err);
      }
    }

    fetchAddress();
  }, [orgId, customer?.addressId]);

  // Set up map marker when address is loaded with coordinates
  useEffect(() => {
    if (!customer || !address) {
      setCustomerMarker(null);
      return;
    }

    if (address.latitude && address.longitude) {
      setCustomerMarker({
        id: customer.id,
        name: customer.fullName,
        latitude: address.latitude,
        longitude: address.longitude,
        address: address.formattedAddress,
      });
    }
  }, [customer, address]);

  if (!customer) {
    return null;
  }

  // Get city/locality from address for display
  const getCity = () => {
    if (!address) return null;
    const details = address.details as Record<string, unknown> | null;
    if (!details) return null;
    if ('city' in details && typeof details.city === 'string') return details.city;
    if ('district' in details && typeof details.district === 'string') return details.district;
    return null;
  };

  const formatAddress = () => {
    if (!address) return t('customers.noAddressSpecified');
    return address.formattedAddress;
  };

  return (
    <div className={`flex flex-col ${customerMarker ? 'xl:flex-row' : ''} gap-6`}>
      {/* Left side - Customer details */}
      <div className={`space-y-6 ${customerMarker ? 'xl:flex-1' : ''}`}>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <User className="w-4 h-4" />
              <span>{t('customers.name')}</span>
            </div>
            <p className="text-lg font-medium">{customer.fullName}</p>
          </div>

          <div className="rounded-lg border p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <Phone className="w-4 h-4" />
              <span>{t('customers.phone')}</span>
            </div>
            <p className="text-lg font-medium">
              {customer.phone || t('customers.notSpecified')}
            </p>
          </div>

          <div className="rounded-lg border p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <MapPin className="w-4 h-4" />
              <span>{t('customers.city')}</span>
            </div>
            <p className="text-lg font-medium">
              {getCity() || t('customers.notSpecified')}
            </p>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">{t('customers.customerInformation')}</h3>
          <dl className="space-y-3">
            <div className="flex border-b pb-2">
              <dt className="w-40 text-muted-foreground">{t('customers.fullName')}</dt>
              <dd className="flex-1 font-medium">{customer.fullName}</dd>
            </div>
            <div className="flex border-b pb-2">
              <dt className="w-40 text-muted-foreground">ID</dt>
              <dd className="flex-1 font-mono text-sm">{customer.id}</dd>
            </div>
            <div className="flex border-b pb-2">
              <dt className="w-40 text-muted-foreground">{t('customers.phone')}</dt>
              <dd className="flex-1">{customer.phone || t('customers.notSpecified')}</dd>
            </div>
            <div className="flex border-b pb-2">
              <dt className="w-40 text-muted-foreground">{t('customers.address')}</dt>
              <dd className="flex-1">{formatAddress()}</dd>
            </div>
            <div className="flex border-b pb-2">
              <dt className="w-40 text-muted-foreground">{t('customers.registrationStatus')}</dt>
              <dd className="flex-1">
                {customer.isRegistered ? (
                  <span className="inline-flex items-center gap-1.5 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    {t('customers.registered')}
                  </span>
                ) : (
                  <span className="text-muted-foreground">{t('customers.notRegistered')}</span>
                )}
              </dd>
            </div>
          </dl>
        </div>

        {/* Signup QR Code Section - only for unregistered customers */}
        {!customer.isRegistered && (
          <div className="rounded-lg border p-4">
            <h3 className="text-lg font-semibold mb-2">{t('customers.customerSignup')}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('customers.signupQrDescription')}
            </p>
            <Button onClick={handleGenerateQr} variant="outline">
              <QrCode className="w-4 h-4 mr-2" />
              {t('customers.generateSignupQr')}
            </Button>
          </div>
        )}
      </div>

      {/* QR Code Dialog */}
      <Dialog open={showQrDialog} onOpenChange={setShowQrDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('customers.signupQrCode')}</DialogTitle>
            <DialogDescription>
              {t('customers.signupQrInstructions')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-4">
            {qrLoading && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('common:labels.loading')}
              </div>
            )}
            {qrError && (
              <div className="text-destructive text-center">{qrError}</div>
            )}
            {qrData && (
              <>
                <div className="bg-white p-4 rounded-lg border mb-4">
                  <img
                    src={qrData.qrCodeBase64}
                    alt="Signup QR Code"
                    className="w-48 h-48"
                  />
                </div>
                <p className="text-sm text-muted-foreground text-center break-all">
                  {qrData.signupUrl}
                </p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Right side - Customer Location Map */}
      {customerMarker && (
        <div className="xl:w-[28rem] xl:shrink-0">
          <div className="xl:sticky xl:top-4">
            <h3 className="text-lg font-semibold mb-4">{t('customers.location')}</h3>
            <DeliveryMap
              customers={[customerMarker]}
              className="h-64 xl:h-80 rounded-lg overflow-hidden border"
            />
          </div>
        </div>
      )}
    </div>
  );
}
